import { signal, computed, Signal, WritableSignal } from '@angular/core';

/**
 * Base state service that provides Signal-based state management.
 * This service can be extended by specific feature state services.
 */
export abstract class StateService<T extends Record<string, any>> {
  protected state: WritableSignal<T>;

  constructor(initialState: T) {
    this.state = signal<T>(initialState);
  }

  /**
   * Get the current state value
   */
  getState(): T {
    return this.state();
  }

  /**
   * Get a signal for the entire state
   */
  select(): Signal<T> {
    return this.state.asReadonly();
  }

  /**
   * Get a computed signal for a specific slice of the state.
   * Pass an `equal` function for collection-type slices to prevent
   * unnecessary change notifications when the data hasn't changed.
   */
  selectSlice<K>(
    selector: (state: T) => K,
    options?: { equal: (a: K, b: K) => boolean }
  ): Signal<K> {
    return computed(() => selector(this.state()), options);
  }

  /**
   * Update the state
   * @param stateOrUpdaterFn Partial state or updater function
   */
  setState(stateOrUpdaterFn: Partial<T> | ((state: T) => Partial<T>)): void {
    const updatedState =
      typeof stateOrUpdaterFn === 'function' ? stateOrUpdaterFn(this.getState()) : stateOrUpdaterFn;

    this.state.update(state => ({ ...state, ...updatedState }));
  }

  /**
   * Reset the state to its initial value
   * @param initialState Initial state to reset to
   */
  resetState(initialState: T): void {
    this.state.set(initialState);
  }
}
